import { Button, ClickEvent, ContextMenuItemCreator, SelectionType, Snackbar, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, TableSelectionEvent } from "@mcleod/components";
import { LogManager, ModelRow, RowUpdateEvent, SortDirection, SortUtil } from "@mcleod/core";
import { LMEUserSettings } from "@mcleod/general";
import { AutogenLayoutCarrierSalesBoard } from "@mcleod/crm/src/autogen/AutogenLayoutCarrierSalesBoard";
import { ModelCarrierSalesProfile, RowCarrierSalesProfile } from "@mcleod/crm"
import { ModelCarrierAction, RowCarrierAction } from "./models/ModelCarrierAction";
import { CarrierSalesExpand } from "./CarrierSalesExpand";
import { CarrierAction } from "./CarrierAction";
import { CommonTooltips } from "@mcleod/common";


const log = LogManager.getLogger("lme.powerbroker.crm.CarrierSalesBoard");

export class CarrierSalesBoard extends AutogenLayoutCarrierSalesBoard {

    private _carrierSalesProfile: string;
    private _profileRow: RowCarrierSalesProfile;

    onLoad(): void | Promise<void> {
        this.layoutCarrierSalesDetail.visible = false;
        this.splitterSalesBoard.buttonExpandFirst.clicked();
    }

    tableCarrierSalesActionsOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const expandLayout = event.expandComponents[0] as CarrierSalesExpand;
        expandLayout.search({"id" : modelRow.get("prospect_action_id")})
        .then(() => {
            expandLayout.activeRow.set("status", "C");
            expandLayout.activeRow.set("action_date", new Date());
            expandLayout.mainDataSource.activeRow.addAfterPostListener((event: RowUpdateEvent) =>
                this.mainDataSource.search({ profile_row: this.profileRow })
                .then(() => Promise.resolve())
            )
        });
    }

    tableCarrierSalesActionsOnSelect(event: TableSelectionEvent) {
        if (!!event.newRow) {
            const row = event.newRow as TableRow;
            const modelRow = row.data as ModelRow;
            const carrierId = modelRow.get("carrier_id");
            this.layoutCarrierSalesDetail.carrierId = carrierId;
            this.layoutCarrierSalesDetail.visible = true;
            if (!!this.splitterSalesBoard["_expandedFirstComponent"]) {
                this.splitterSalesBoard.buttonExpandSecond.clicked();
            }
        } else {
            this.layoutCarrierSalesDetail.visible = false;
            this.splitterSalesBoard.buttonExpandFirst.clicked();
        }
        this.layoutCarrierSalesToolPanel.tableCarrierSalesOnSelect(event);
    }

    tableCarrierSalesActionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        const labelCarrierName = tableRow.findComponentById("labelCarrierName");
        if (labelCarrierName) {
            CommonTooltips.setTooltipContentLayout(labelCarrierName, modelRow.getString("carrier_id"), "lme/powerbroker/CarrierQuickInfo");
        }

        tableRow.expandable = !tableRow.data.isNull("prospect_action_id");
        tableRow.contextMenuItems = () => {
            if (tableRow.table.selectedRow !== tableRow) {
                tableRow.table.selectRow(tableRow, SelectionType.SINGLE, null);
            }
            const buttons:any[] = tableRow.data.isNull("prospect_action_id") ? []
                : [{
                    visible: true,
                    text: "Complete Action",
                    button: this.createCompleteActionMenuItem(tableRow.data.getString("prospect_action_id"), modelRow)
                }]
            buttons.push(
                {button: this.layoutCarrierSalesToolPanel.buttonCapacity, text: "Carrier Capacity"},
                {button: this.layoutCarrierSalesToolPanel.buttonLaneHistory, text: "Lane History"},
                {button: this.layoutCarrierSalesToolPanel.buttonAvailableLoads, text: "Available Loads"}
            );
            return ContextMenuItemCreator.fromButtons(...buttons);
        }
    }

    async tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;        
        if (modelRow.isNull("prospect_action_id")) {
            return this.showAddRecurringCallActivitySlideout(modelRow);
        } else {
            return CarrierAction.showEditActionSlideout(modelRow, {
                onSave: (updatedRow) => updatedRow.post().then(() => this.mainDataSource.search({ profile_row: this.profileRow })),
                onDelete: () => this.mainDataSource.search({ profile_row: this.profileRow })
            });
        }
    }

    async showAddRecurringCallActivitySlideout(modelRow: ModelRow) {
        CarrierAction.showAddActionSlideout({
            "date_due": modelRow.getDate("date_due"),
            "status": "C",
            "activity_id": modelRow.get("activity_id"),
            "descr": modelRow.getString("activity"),
            "salesperson_id": modelRow.getString("salesperson_id"),
            "carrier_id": modelRow.getString("carrier_id"),
            "primary_contact": modelRow.getString("contact"),
            "phone": modelRow.getString("contact_phone"),
            "email": modelRow.getString("contact_email"),
            "operations_user": modelRow.getString("carrier_mgr")
        }, {
            doAfterSlideIn: (slideout) => {
                const layout = slideout.layout as CarrierAction;
                layout.textboxCarrierId.enabled = false;
                layout.activeRow.setLookupModelData("activity_id", {"id": modelRow.getString("activity_id")});
                layout.activeRow.setLookupModelData("primary_contact", {"name": modelRow.getString("contact")});
                layout.activeRow.setLookupModelData("salesperson_id", {"name": modelRow.getString("salesperson")});
                layout.activeRow.setLookupModelData("carrier_id", {"name": modelRow.getString("carrier_name")});
                layout.activeRow.setLookupModelData("operations_user", {"name": modelRow.getString("carrier_mgr_name")});
                layout.mainDataSource.displayDataInBoundComponents();
                layout.initializeContactFilter(modelRow.getString("carrier_id"));
            },
            onSave: (updatedRow) => updatedRow.post().then(() => this.mainDataSource.search({ profile_row: this.profileRow }))
        });
    }

    createCompleteActionMenuItem(actionId: string, rowToRemove: ModelRow): Button {
        return new Button({
            caption: "Complete Action",
            onClick: () => {
                const progressSnackbar = Snackbar.showDownloadSnackbar("Action in Progress", "Marking complete...", { persist: true });
                return new ModelCarrierAction().searchSingle({ id: actionId })
                .then((actionRow: RowCarrierAction) => {
                    actionRow.set("status", "C");
                    actionRow.set("action_date", new Date());
                    return actionRow.post()
                    .then(() => this.tableCarrierSalesActions.removeRow(rowToRemove));
                })
                .finally(() => progressSnackbar.dismiss())
            }
        });
    }

    findNextActivity(carrierId:string, activityId: string, dateDue: Date): {id: string, descr: string, date_due: Date} {
        const carrierActivities = this.mainDataSource.data
            .filter((row:ModelRow) => carrierId === row.get("carrier_id") && row.isNull('action_date'))
            .sort((a:ModelRow, b:ModelRow) => {
                const sortInfo:{fieldName: string, sort: SortDirection}[] = [
                    {fieldName: "date_due", sort: "asc"},
                    {fieldName: "priority", sort: "desc"}
                ]
                for (const fieldSortInfo of sortInfo) {
                    const valueA = SortUtil.getSortTestValue(a, fieldSortInfo.fieldName);
                    const valueB = SortUtil.getSortTestValue(b, fieldSortInfo.fieldName);
                    const compareResult = SortUtil.compareTwoValues(valueA, valueB, fieldSortInfo.sort, false);
                    if (compareResult !== 0)
                        return compareResult;
                }
                return 0;
            });
        let i = 0;
        let rowMatch = false;
        while(!rowMatch && i < carrierActivities.length) {
            const activityRow = carrierActivities[i++];
            if (activityId === activityRow.getString("activity_id")
            && dateDue.getTime() === activityRow.getDate("date_due").getTime()) {
                rowMatch = true;
            }
        }
        const nextActivity = carrierActivities[i];
        return {
            id: nextActivity?.getString("activity_id"),
            descr: nextActivity?.getString("activity"),
            date_due: nextActivity?.getDate("date_due")
        };
    }

    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile")
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? this.carrierSalesProfile ?? LMEUserSettings.get()?.carrier_call_list_id;
        if (profileId == null)
            Snackbar.showSnackbar("You do not have a default carrier sales profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        else {
            const profileRow = await new ModelCarrierSalesProfile().searchSingle({ id: profileId });
            if (profileRow == null)
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            else
                this.setProfileRow(profileRow);
        }
    }

    public setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            this._profileRow = value;
            if (this._profileRow != null) {
                this.searchMainDatasource();
            }
        }
    }

    searchMainDatasource() {
        this.mainDataSource.search({
            profile_row: this.profileRow
        });
    }

    override get windowTitle(): string {
        const title = "Carrier Sales Board";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    public get carrierSalesProfile(): string {
        return this._carrierSalesProfile;
    }
    public set carrierSalesProfile(value: string) {
        this._carrierSalesProfile = value;
    }
    public get profileRow(): RowCarrierSalesProfile {
        return this._profileRow;
    }
    public set profileRow(value: RowCarrierSalesProfile) {
        this._profileRow = value;
    }
}
